<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useListStoreNew } from "@/stores/ListStoreNew";
import { usePagination } from "@/composables/usePagination";
import { useVacancyStore } from "@/stores/VacancyStore";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const list = useListStoreNew();
list.$reset();

const route = useRoute();
const router = useRouter();
const vacancyStore = useVacancyStore();
const filter = ref(route.query?.af ?? "new");
const statuses = ["all", "new", "reviewing", "parked", "converted"];

const { setUrl, changePage } = usePagination(list, filter);

const applyFilter = () => {
  list.clearFilters();
  if (filter.value && filter.value !== "all") {
    list.addFilter("status", filter.value);
  }
};

const load = async () => {
  applyFilter();
  list.resetPageWithoutFetching();
  setUrl();
  await list.fetchIndex();
};

const setFilter = async (status) => {
  filter.value = status;
  await load();
};

const formatName = (row) => {
  const name = `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
  return name || row?.contact?.name || row?.email || "Unknown";
};

const view = (row) => {
  router.push({
    name: "vacancies-view",
    params: { id: row.id },
  });
};

onMounted(async () => {
  list.configure({ remote: "/api/vacancy", local: "/vacancies" });
  vacancyStore.fetchNewCount({ force: true });
  await load();
});
</script>

<template>
  <div>
    <div class="box mb-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h1 class="text-xl font-semibold">Vacancies</h1>
      </div>
      <div class="mt-4 flex flex-wrap gap-2">
        <button
          v-for="status in statuses"
          :key="status"
          class="btn-plain"
          :class="filter === status ? 'bg-indigo-500' : 'bg-slate-500'"
          @click.prevent="setFilter(status)"
        >
          {{ status }}
        </button>
      </div>
    </div>

    <table class="box list" v-if="!list.isLoading">
      <tbody>
        <tr
          v-for="row in list.getIndex"
          :key="row.id"
          class="border-b border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
          @click.prevent="view(row)"
        >
          <td class="p-3 font-semibold">{{ formatName(row) }}</td>
          <td class="p-3 hidden md:table-cell">{{ row.position || "-" }}</td>
          <td class="p-3 hidden lg:table-cell">{{ row.email || row.contact?.email || "-" }}</td>
          <td class="p-3 hidden md:table-cell">{{ row.phone || row.contact?.phone || "-" }}</td>
          <td class="p-3 text-right hidden sm:table-cell">
            {{ dayjs(row.submitted_at || row.created_at).fromNow() }}
          </td>
          <td class="p-3 text-right uppercase text-xs">{{ row.status }}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="6" class="p-4" v-if="list.getIndex.length < 1">No vacancies to display</td>
        </tr>
        <tr>
          <th colspan="6" class="p-4">
            <PrevNextButtons
              :showPrev="list.showPrev"
              :showNext="list.showNext"
              @prev="changePage(-1)"
              @next="changePage(1)"
            />
          </th>
        </tr>
      </tfoot>
    </table>
    <table class="box list" v-else>
      <tbody>
        <tr v-for="n in 20" :key="n" class="border-b border-gray-300 animate-pulse">
          <td class="p-3"><div class="h-6 bg-gray-300 rounded"></div></td>
          <td class="p-3 hidden md:table-cell"><div class="h-6 bg-gray-300 rounded"></div></td>
          <td class="p-3 hidden lg:table-cell"><div class="h-6 bg-gray-300 rounded"></div></td>
          <td class="p-3 hidden md:table-cell"><div class="h-6 bg-gray-300 rounded"></div></td>
          <td class="p-3 hidden sm:table-cell"><div class="h-6 bg-gray-300 rounded"></div></td>
          <td class="p-3"><div class="h-6 bg-gray-300 rounded"></div></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
